/**
 * Tenancy calls (partner channel): who am I / which workspaces, invite acceptance.
 * All requests carry the signed-in user's token; the server + database enforce access.
 */
import axios from 'axios';
import type { FirmCapitalMarketAssumptions } from '../../../shared/src/calculation/investmentProfiles.ts';
import type { AxiosError } from 'axios';
import { getAccessToken } from './authClient.ts';
import { apiBaseUrl } from './apiBase.ts';

/** A signed-in user's workspace membership (server/src/shared/clientProfileRepo.ts's Membership). */
export interface Membership {
  tenantId: string;
  role: 'owner' | 'adviser' | 'staff' | 'client';
  tenantName: string;
  tenantKind: 'direct' | 'partner';
}

/**
 * What kind of firm a partner is. Only 'adviser' gives its own advice under its own AFSL; for every
 * other value the advice is AgedCareWise's and the firm refers the client to it. The finer
 * distinction is for the firm's records and for reporting — it decides nothing beyond the label.
 */
export type Profession = 'adviser' | 'accountant' | 'solicitor' | 'aged_care_provider' | 'support_at_home_provider' | 'super_fund' | 'other';

/** How each profession reads on screen. */
export const PROFESSION_LABELS: Record<Profession, string> = {
  adviser: 'Financial adviser',
  accountant: 'Accountant',
  solicitor: 'Solicitor',
  aged_care_provider: 'Aged-care provider',
  support_at_home_provider: 'Support at Home provider',
  super_fund: 'Superannuation trustee',
  other: 'Other professional',
};

/**
 * What a firm calls the people who hold clients. A role name is the most-repeated copy in the
 * product, and calling an accountant an "adviser" quietly contradicts the terms they signed.
 * "Client lead" is the fallback where the profession has no obvious word for it: it names the
 * relationship without implying anything about advice.
 */
export function holderLabelFor(profession: Profession | null | undefined): string {
  switch (profession) {
    case 'adviser':
      return 'Adviser';
    case 'accountant':
      return 'Accountant';
    case 'solicitor':
      return 'Solicitor';
    default:
      return 'Client lead';
  }
}

/** Full firm record — server/src/rest/tenants/tenants.ts's tenantSchema. */
export interface Tenant {
  id: string;
  kind: 'direct' | 'partner';
  name: string;
  /** Null on direct tenants and on firms created before it was recorded. */
  profession: Profession | null;
  afslLicenseeName: string | null;
  afslNumber: string | null;
  /**
   * How this firm's advice is provided. Not "are you licensed?" — for an advice firm that is always
   * yes and says nothing. The three answers differ in who the advice document has to name, and an
   * own_afsl firm's people are representatives of the licensee who must NOT be called authorised
   * representatives. Null on firms that pre-date the question, and on non-adviser firms.
   */
  adviceStructure: 'own_afsl' | 'corporate_ar' | 'individual_ar' | null;
  /** The corporate authorised representative's registered name, where that is the structure. */
  carName: string | null;
  /** The CAR's own ASIC number — a company's, distinct from any individual's. */
  carNumber: string | null;
  branding: Record<string, unknown>;
  /** How the firm works (not how it looks) — see FirmSettings. */
  settings?: FirmSettings;
  /** Our standard wording, served by the API so the client never holds a second copy to drift. */
  messageDefaults?: MessageDefaults;
  /**
   * Whether THIS server chases unresponsive clients, and how many times. Served rather than assumed,
   * because the scheduler is off unless AUTO_REMINDERS=on — a screen that guessed would promise an
   * adviser their client was being chased on a deployment where nothing runs.
   */
  autoReminders?: { enabled: boolean; maxPerClient: number };
  isolationTier: number;
  status: string;
}

/** The wording used when a firm hasn't written its own. Served, never duplicated client-side. */
export interface MessageDefaults {
  invite: { subject: string; body: string };
  reminder: { subject: string; body: string };
}

/** Firm working preferences, stored on the tenant. */
export interface FirmSettings {
  /** New client invites are pre-set to chase after this many days. null = don't chase automatically. */
  defaultFollowUpDays?: number | null;
  /** The firm's own wording for the emails clients receive. Absent fields fall back to the defaults. */
  templates?: { invite?: { subject?: string; body?: string }; reminder?: { subject?: string; body?: string } };
  /** Links, not uploads — the firm hosts these, so a client always gets the current version. */
  fsgUrl?: string;
  companyProfileUrl?: string;
  /** The firm's own capital market assumptions — the table every projection under this firm runs
   *  on. null = explicitly cleared back to the AgedCareWise defaults; absent = never set. */
  capitalMarketAssumptions?: FirmCapitalMarketAssumptions | null;
}

/** Save firm working preferences. Merged server-side, so this can send just what changed. */
export async function updateFirmSettings(tenantId: string, settings: FirmSettings): Promise<{ ok: boolean; message?: string }> {
  try {
    const headers = await authHeaders();
    await axios.put(`${getApiBaseUrl()}/rest/tenants/${tenantId}/settings`, { settings }, { headers, timeout: 30_000 });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not save those settings.') };
  }
}

interface TenantErrorBody {
  error?: { message?: string };
}

function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

async function authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/**
 * Turn an axios failure into a message that actually helps: distinguish "can't reach the server"
 * (no response — server down / offline) from a real server-side reason, and surface that reason.
 */
function explainError(err: AxiosError<TenantErrorBody> | Error | unknown, fallback: string): string {
  const axiosErr = err as AxiosError<TenantErrorBody> | undefined;
  const serverMsg = axiosErr?.response?.data?.error?.message;
  if (serverMsg) return serverMsg;
  if ((err as Error)?.message === 'Not signed in') return 'Your session has expired — please sign in again.';
  if (!axiosErr?.response) {
    return "We couldn't reach AgedCareWise. Check your internet connection and try again in a moment.";
  }
  return fallback;
}

/**
 * Somebody in the firm.
 *
 * `plan` is THEIR OWN client allowance, not the firm's: an adviser's included clients are not
 * spendable by a colleague. Null for support, who hold no clients and so need no plan — which is
 * also why support can never be used as a cheap adviser.
 */
export interface FirmMember {
  userId: string;
  role: 'owner' | 'adviser' | 'staff' | 'client';
  status: 'active' | 'invited' | 'disabled';
  createdAt: string;
  email: string | null;
  /** Their ASIC representative number, as it appears on the Financial Advisers Register. */
  adviserNumber: string | null;
  /** Client files naming them as the responsible adviser. */
  clientCount: number;
  plan: { mode: 'annual' | 'payg' | 'comp' | 'none'; used: number; capacity: number | null; remaining: number | null } | null;
}

/** The firm's roster, newest last. Any member may read it. */
export async function listFirmMembers(tenantId: string): Promise<FirmMember[]> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/tenants/${tenantId}/members`, { headers, timeout: 30_000 });
  return (res.data.members as FirmMember[]) || [];
}

/**
 * Invite a colleague. `adviser` holds their own clients and needs a plan before they can open one;
 * `staff` is support — free, holds nothing, helps with anyone's files.
 *
 * The token comes back so the principal can pass the link on directly. Colleague invites are not
 * emailed by the server (only client invites are), so that link is the only way in.
 */
export async function inviteColleague(
  tenantId: string,
  email: string,
  role: 'adviser' | 'staff',
): Promise<{ ok: true; inviteId: string; token: string; expiresAt: string } | { ok: false; message: string }> {
  try {
    const headers = await authHeaders();
    const res = await axios.post(`${getApiBaseUrl()}/rest/tenants/${tenantId}/invites`, { email, role }, { headers, timeout: 30_000 });
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not send that invite.') };
  }
}

/**
 * Change one member. The server decides what the caller may do: anyone may set their own ASIC
 * number, only the principal may set someone else's or change what they do.
 *
 * Refusals arrive with an explanation worth showing verbatim — "reassign this person's 3 client(s)
 * before making them support" is the whole instruction.
 */
export async function updateFirmMember(
  tenantId: string,
  userId: string,
  patch: { adviserNumber?: string | null; role?: 'adviser' | 'staff' },
): Promise<{ ok: boolean; message?: string }> {
  try {
    const headers = await authHeaders();
    await axios.patch(`${getApiBaseUrl()}/rest/tenants/${tenantId}/members/${userId}`, patch, { headers, timeout: 30_000 });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not save that change.') };
  }
}

/** Remove someone. Refused while they still hold clients — those must be reassigned first. */
export async function removeFirmMember(tenantId: string, userId: string): Promise<{ ok: boolean; message?: string }> {
  try {
    const headers = await authHeaders();
    await axios.delete(`${getApiBaseUrl()}/rest/tenants/${tenantId}/members/${userId}`, { headers, timeout: 30_000 });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not remove that person.') };
  }
}

/**
 * Hand the account to another adviser on the team. Never to an email address — that is how an
 * account goes to a typo, and the outgoing principal cannot undo it.
 */
export async function transferOwnership(
  tenantId: string,
  toUserId: string,
  outgoing: 'adviser' | 'leave',
): Promise<{ ok: boolean; message?: string }> {
  try {
    const headers = await authHeaders();
    await axios.post(`${getApiBaseUrl()}/rest/tenants/${tenantId}/transfer-ownership`, { toUserId, outgoing }, { headers, timeout: 30_000 });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not hand the account over.') };
  }
}

/** Move a client file to another adviser. Does not re-bill: the client was taken on once. */
export async function reassignClient(profileId: string, adviserUserId: string): Promise<{ ok: boolean; message?: string }> {
  try {
    const headers = await authHeaders();
    await axios.post(`${getApiBaseUrl()}/rest/profiles/${profileId}/reassign`, { adviserUserId }, { headers, timeout: 30_000 });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not reassign that client.') };
  }
}

/**
 * Who in the firm can see one client file. 'restricted' means the responsible adviser and the
 * principal only — support does not see it at all, because the existence of the file is the
 * sensitive part.
 */
export async function setClientVisibility(
  profileId: string,
  visibility: 'firm' | 'restricted',
  note?: string | null,
): Promise<{ ok: boolean; message?: string }> {
  try {
    const headers = await authHeaders();
    await axios.post(`${getApiBaseUrl()}/rest/profiles/${profileId}/visibility`, { visibility, note: note ?? null }, { headers, timeout: 30_000 });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not change who can see this client.') };
  }
}

/** Save the firm's licensing — how its advice is provided, and the entities an SOA must name. */
export async function updateFirmLicensing(
  tenantId: string,
  patch: {
    afslLicenseeName?: string;
    afslNumber?: string;
    adviceStructure?: Tenant['adviceStructure'];
    carName?: string | null;
    carNumber?: string | null;
  },
): Promise<{ ok: boolean; message?: string }> {
  try {
    const headers = await authHeaders();
    await axios.put(`${getApiBaseUrl()}/rest/tenants/${tenantId}/settings`, patch, { headers, timeout: 30_000 });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not save your licensing details.') };
  }
}

/** The signed-in user's workspaces: [{ tenantId, role, tenantName, tenantKind }]. */
export async function getMyMemberships(): Promise<Membership[]> {
  const headers = await authHeaders();
  // 60s (not 30s): this call decides where a partner lands at sign-in, and the first request to a
  // sleeping free-tier server can take 30–50s to wake. A too-short timeout here misroutes partners.
  const res = await axios.get(`${getApiBaseUrl()}/rest/me`, { headers, timeout: 60_000 });
  return res.data.memberships || [];
}

/** The user's partner-firm membership (owner/adviser/staff of a partner tenant), or null. */
export async function getPartnerMembership(): Promise<Membership | null> {
  const memberships = await getMyMemberships();
  return (
    memberships.find((m) => m.tenantKind === 'partner' && ['owner', 'adviser', 'staff'].includes(m.role)) || null
  );
}

interface RegisterPartnerParams {
  name: string;
  profession: Profession;
  afslLicenseeName?: string;
  afslNumber?: string;
  /** How the firm's advice reaches the client — decides which entities an SOA must name. */
  adviceStructure?: Tenant['adviceStructure'];
  /** The PRACTICE's ASIC number, where the practice is a corporate authorised representative. */
  carName?: string;
  carNumber?: string;
  /** The SIGNER's own ASIC number — a different entity's from carNumber, and it follows the person. */
  adviserNumber?: string;
}

/** Create the signed-in user's own partner firm (wizard submit). Pending until Jon approves. */
export async function registerPartner({
  name,
  profession,
  afslLicenseeName,
  afslNumber,
  adviceStructure,
  carName,
  carNumber,
  adviserNumber,
}: RegisterPartnerParams): Promise<{ ok: true; tenantId: string; status: string } | { ok: false; message: string }> {
  try {
    const headers = await authHeaders();
    const res = await axios.post(
      `${getApiBaseUrl()}/rest/partners/register`,
      {
        name,
        profession,
        // Empty strings are omitted, not sent: the API rejects a blank where it expects a number or
        // nothing, and "" would also read as a recorded-but-empty value on the firm.
        ...(profession === 'adviser'
          ? {
              afslLicenseeName,
              afslNumber,
              ...(adviceStructure ? { adviceStructure } : {}),
              ...(carName ? { carName } : {}),
              ...(carNumber ? { carNumber } : {}),
              ...(adviserNumber ? { adviserNumber } : {}),
            }
          : {}),
      },
      { headers, timeout: 30_000 },
    );
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Registration failed — please try again.') };
  }
}

/** Full firm record (name, profession, licensing attribution, branding) — members only. */
export async function getTenant(tenantId: string): Promise<Tenant | null> {
  try {
    const headers = await authHeaders();
    const res = await axios.get(`${getApiBaseUrl()}/rest/tenants/${tenantId}`, { headers, timeout: 30_000 });
    // The standard message wording rides along on the tenant fetch the portal already makes, so the
    // Settings screen can SHOW an adviser what we send rather than describing it.
    return { ...res.data.tenant, messageDefaults: res.data.messageDefaults, autoReminders: res.data.autoReminders };
  } catch {
    return null;
  }
}

/** Adviser invites a client into the firm (one-time link; only its hash is stored server-side). */
/**
 * Send a client their invite link again.
 *
 * A reminder is a FRESH one-time link, not a copy of the old one: the original token is stored only
 * as a hash and cannot be recovered. The server reads the recipient from the existing invite, so a
 * reminder can never be pointed at a different address from here.
 */
export async function resendClientInvite(
  tenantId: string,
  inviteId: string,
): Promise<{ ok: true; emailed: boolean; expiresAt: string; token?: string } | { ok: false; message: string }> {
  try {
    const headers = await authHeaders();
    const res = await axios.post(
      `${getApiBaseUrl()}/rest/tenants/${tenantId}/invites/${inviteId}/resend`,
      {},
      { headers, timeout: 30_000 },
    );
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not send the reminder.') };
  }
}

export async function createClientInvite(tenantId: string, email: string, profileId: string | null = null, followUpDays: number | null = null): Promise<{ ok: true; inviteId: string; token: string; expiresAt: string; emailed: boolean } | { ok: false; message: string }> {
  try {
    const headers = await authHeaders();
    const res = await axios.post(
      `${getApiBaseUrl()}/rest/tenants/${tenantId}/invites`,
      { email, role: 'client', ...(profileId ? { profileId } : {}), ...(followUpDays ? { followUpDays } : {}) },
      { headers, timeout: 30_000 },
    );
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, message: explainError(err, 'Could not create the invite.') };
  }
}

/** Redeem a one-time invite token. Returns { ok, tenantId, role } or { ok:false, message }. */
export async function acceptInvite(token: string): Promise<{ ok: true; tenantId: string; role: string } | { ok: false; message: string }> {
  try {
    const headers = await authHeaders();
    const res = await axios.post(`${getApiBaseUrl()}/rest/invites/accept`, { token }, { headers, timeout: 30_000 });
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, message: explainError(err, 'This invite link is not valid.') };
  }
}

const PENDING_INVITE_KEY = 'awisePendingInvite';

/** Capture ?invite=TOKEN from the address bar (kept until the user has signed in). */
export function capturePendingInviteFromUrl(): string | null {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('invite');
    if (token) {
      sessionStorage.setItem(PENDING_INVITE_KEY, token);
      url.searchParams.delete('invite');
      window.history.replaceState({}, '', url.toString());
    }
  } catch {
    /* no-op: URL handling must never break app start-up */
  }
  return sessionStorage.getItem(PENDING_INVITE_KEY);
}

export function getPendingInvite(): string | null {
  return sessionStorage.getItem(PENDING_INVITE_KEY);
}

export function clearPendingInvite(): void {
  sessionStorage.removeItem(PENDING_INVITE_KEY);
}

/**
 * Read the pending invite AND clear it, in one step. A one-time token must be redeemed once: three
 * handlers can run on the way in (already signed in, just signed in, just signed up) and two of them
 * running together would each read the token, each call the server, and the second would be told
 * "already used" — shown to the client as a failed invite over a link that had in fact worked
 * (owner, 16 Oct 2026). Whoever takes it first redeems it; the next finds nothing.
 */
export function takePendingInvite(): string | null {
  const token = sessionStorage.getItem(PENDING_INVITE_KEY);
  if (token) {
    sessionStorage.removeItem(PENDING_INVITE_KEY);
    sessionStorage.setItem(REDEEMING_INVITE_KEY, '1');
  }
  return token;
}

const REDEEMING_INVITE_KEY = 'awiseInviteRedeeming';

/**
 * True from the moment a token is taken until the handler that took it has finished — so the
 * auto-resume effect, which fires as soon as the session is set, does not run a second resume and
 * land the client on the dashboard while the invite handler is still on its way to step 1.
 */
export function inviteInProgress(): boolean {
  return sessionStorage.getItem(REDEEMING_INVITE_KEY) === '1';
}

export function finishInviteRedemption(): void {
  sessionStorage.removeItem(REDEEMING_INVITE_KEY);
}
